import { Error } from '@mui/icons-material'
import { Box, InputAdornment, Slider, Stack, TextField, Typography } from '@mui/material'
import { useController } from '../../hooks/useController'
import { useTexts } from '../../useTexts'
import { StorageAlert } from './StorageAlert'
import { useRangePicker } from './useRangePicker'

export const RangePicker = () => {
  const texts = useTexts()
  const {
    maxPlanningRange,
    min,
    handleSliderChange,
    handleSliderChangeCommitted,
    handleSliderMouseDown,
    handleInputChange,
    generateDayLabels,
    generateSliderMarks,
    inputValue,
    inputError,
    storageError,
    newPlanningRange,
    isLoading,
    isDragging,
  } = useRangePicker()
  const { data } = useController()
  const { percentage } = data

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
        {texts.rangePickerTitle}
      </Typography>

      <Stack direction="row" spacing={4} alignItems="flex-start">
        {/* Slider for selecting range */}
        <Box sx={{ flex: 1 }}>
          <Slider
            value={newPlanningRange}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            onMouseDown={handleSliderMouseDown}
            min={min}
            max={maxPlanningRange}
            marks={generateSliderMarks()}
            valueLabelDisplay="auto"
            color="primary"
            sx={{
              height: 6,
              mb: 1,
              mt: 2,
            }}
          />

          {/* Day labels below slider */}
          <Box
            sx={{
              position: 'relative',
              px: 0.5,
              mt: 0.5,
            }}
          >
            {generateDayLabels().map((days, index) => {
              // Calculate position based on actual day value relative to the range
              const position = ((days - min) / (maxPlanningRange - min)) * 100

              return (
                <Typography
                  key={index}
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    position: 'absolute',
                    left: `${position}%`,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {days} days
                </Typography>
              )
            })}
          </Box>
        </Box>

        {/* Input field for manual entry */}
        <Box>
          <TextField
            value={inputValue}
            onChange={handleInputChange}
            type="number"
            error={inputError.hasError}
            sx={{ width: 100 }}
            inputProps={{
              min,
              max: maxPlanningRange,
            }}
            InputProps={{
              endAdornment: inputError.hasError ? (
                <InputAdornment position="end">
                  <Error sx={{ color: 'error.main', fontSize: 20 }} />
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Stack>

      {/* Storage alert */}
      <StorageAlert
        percentage={percentage}
        newPlanningRange={newPlanningRange}
        isLoading={isLoading}
        isDragging={isDragging}
        inputError={inputError}
        storageError={storageError}
      />
    </Box>
  )
}

export default RangePicker
